import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Stack } from '@spotify-internal/encore-web/ads-business';
import {
  cssColorValue,
  cssSpacing,
  semanticColors,
  Type,
} from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import { NavigationLink } from '@components/shared/ResultsTable/NavigationLink';
import { dateFromProtoTimestamp } from '@helpers/utils';
import { AlbumMetadata } from '@generated/grpc/spotify/artistwatchlist/v1';
import {
  APPROVAL_REASON,
  getPopoverDescription,
  getReasonLabels,
} from '../constants';
import { HeldReleasesTableRowTooltip } from './HeldReleasesTableRowTooltip';

const TextBoxTooltip = styled.div`
  padding: ${cssSpacing('tighter-4', '8px')};
  background-color: ${cssColorValue(
    semanticColors.backgroundElevatedHighlight,
  )};
  display: flex;
  flex-direction: column;
  gap: ${cssSpacing('tighter-5', '4px')};
`;

type DateSubmittedCellProps = {
  row: AlbumMetadata;
};

export const DateSubmittedCell: FunctionComponent<
  DateSubmittedCellProps
> = props => {
  const { row } = props;

  const t = useT();
  const REASON_LABEL = getReasonLabels(t);
  const POPOVER_DESCRIPTION = getPopoverDescription(t);

  if (!!row.dateSubmitted) {
    return (
      <Type variant="viola" semanticColor="textSubdued">
        <HeldReleasesTableRowTooltip
          id="blocked-reason-tooltip"
          overlay={
            <Stack gap={cssSpacing('tighter-3', '12px')}>
              <Type variant="mesto">
                {POPOVER_DESCRIPTION[row.reasonSubmitted as APPROVAL_REASON]}
              </Type>
              <TextBoxTooltip>
                <Type variant="finaleBold">
                  {t(
                    'atlas.held_releases.date_submitted_popover.reason_selected',
                    'Reason selected',
                    'Popover title to show the selected reason of approval',
                  )}
                </Type>
                <Type variant="finale">
                  {REASON_LABEL[row.reasonSubmitted as APPROVAL_REASON]}
                </Type>
              </TextBoxTooltip>
            </Stack>
          }
        >
          {dateFromProtoTimestamp(row.dateSubmitted).date}
        </HeldReleasesTableRowTooltip>
      </Type>
    );
  }
  return (
    <NavigationLink albumUri={row.albumUri!}>
      <Type variant="viola" semanticColor="textSubdued">
        {t(
          'atlas.held_releases.date_submitted_empty_state',
          'Not submitted',
          'Message to show when an album request for approval was not submitted',
        )}
      </Type>
    </NavigationLink>
  );
};
